import { type AppDispatch } from "../../../../Application/globaltypes/redux";
import { pick } from "lodash";
import { batch } from "react-redux";
import { type ActionCreatorWithPayload } from "@reduxjs/toolkit";

import { type FiltersMap, type GenericFiltersMap, mapFilterOption } from "../../../../../utils/filterUtils";
import { type AccountBase, type PackName } from "../../../../../interfaces";
import { PdfFiltersEnum } from "../../types/models";
import {
  setError as setOverviewError,
  setFilterOptions as setOverviewFilterOptions,
  setIsLoading as setOverviewIsLoading,
} from "../slices/pdfFiltersSlice";
import { TagsEnumCamelPlural } from "../../../../../interfaces/TagsEnum";

import {
  setError,
  setFilterOptions,
  setIsLoading,
} from "../../../../People/ContentAssignments/state/slices/contentAssignmentsFiltersSlice";

import objUtils from "../../../../../utils/objectUtils";
import PromiseStatuses from "../../../../../enums/promiseStatuses";
import DataService from "../../../../Application/services/dataServices/typedDataService";

export interface GetFilterOptionsProps {
  showPurchased?: boolean;
  filters?: PdfFiltersEnum[];
}

interface BaseFilterOptionsProps {
  begin: () => void;
  success: (filterOptions: FiltersMap) => void;
  failure: (error: Error) => void;
}

const defaultFiltersOptions = [PdfFiltersEnum.Publishers, PdfFiltersEnum.Tags, PdfFiltersEnum.Packs];

export const getFilterOptions = async (
  props: BaseFilterOptionsProps & GetFilterOptionsProps,
  includePublishers: boolean = true,
) => {
  const { showPurchased, begin, success, failure } = props;
  const selectedFilters = !props.filters?.length ? defaultFiltersOptions : props.filters;

  let filterOptions: FiltersMap = {};

  const tagToFilterMap: { [key in TagsEnumCamelPlural]?: PdfFiltersEnum } = {
    [TagsEnumCamelPlural.Label]: PdfFiltersEnum.Labels,
    [TagsEnumCamelPlural.SoftwareApplication]: PdfFiltersEnum.SoftwareApplications,
  };

  const readPublishersData = async () => {
    const publishers = await DataService.pdfs.getPublishersFilterOptionsAsync();
    filterOptions = mapFilterOption(filterOptions, publishers.data, PdfFiltersEnum.Publishers, (item: PackName) => ({
      text: item.name,
      value: item.id,
    }));
  };

  const readPacksData = async () => {
    const packs = await DataService.packs.getPacksFilterOptions("pdf", !!showPurchased);
    filterOptions = mapFilterOption(filterOptions, packs.data, PdfFiltersEnum.Packs, (item: AccountBase) => ({
      text: item.name,
      value: item.id,
    }));
  };

  const readTagsData = async () => {
    const tags = await DataService.pdfs.getTagsFilterOptionsAsync(!!showPurchased);
    for (const [key, value] of objUtils.typedEntries<TagsEnumCamelPlural, string[]>(tags.data)) {
      if (tagToFilterMap[key]) {
        filterOptions = mapFilterOption(filterOptions, value, tagToFilterMap[key], (item: string) => ({
          text: item,
          value: item,
        }));
      }
    }
  };

  const filterHandlers: { [key in PdfFiltersEnum]?: () => Promise<void> } = {
    [PdfFiltersEnum.Packs]: readPacksData,
    [PdfFiltersEnum.Tags]: readTagsData,
    ...(includePublishers && { [PdfFiltersEnum.Publishers]: readPublishersData }),
  };

  begin();

  const filtersHandlersPromises = Object.values(pick(filterHandlers, selectedFilters)).map(
    (handler: () => Promise<void>) => handler(),
  );

  const result = await Promise.allSettled(filtersHandlersPromises);

  batch(() => {
    let someFulfilled = false;
    result.forEach((item) => {
      ({
        [PromiseStatuses.rejected]: () => failure((item as PromiseRejectedResult).reason),
        [PromiseStatuses.fulfilled]: () => (someFulfilled = true),
      }[item.status]());
    });

    if (someFulfilled) {
      success(filterOptions);
    }
  });
};

const getFilterOptionsWrapper = (
  setIsLoading: ActionCreatorWithPayload<boolean>,
  setFilterOptions: ActionCreatorWithPayload<GenericFiltersMap<string>>,
  setError: ActionCreatorWithPayload<Error>,
) => {
  return (dispatch: AppDispatch) =>
    getFilterOptions({
      showPurchased: true,
      filters: [PdfFiltersEnum.Publishers, PdfFiltersEnum.Tags, PdfFiltersEnum.Packs],
      begin: () => dispatch(setIsLoading(true)),
      success: (filterOptions: FiltersMap) =>
        batch(() => {
          dispatch(setFilterOptions(filterOptions));
          dispatch(setIsLoading(false));
        }),
      failure: (error: Error) =>
        batch(() => {
          dispatch(setError(error));
          dispatch(setIsLoading(false));
        }),
    });
};

export const getOverviewFilterOptions = () =>
  getFilterOptionsWrapper(setOverviewIsLoading, setOverviewFilterOptions, setOverviewError);

export const getContentAssignmentPdfFilterOptions = () => {
  return (dispatch: AppDispatch) =>
    getFilterOptions({
      showPurchased: true,
      filters: [PdfFiltersEnum.Publishers, PdfFiltersEnum.Tags, PdfFiltersEnum.Packs],
      begin: () => {
        dispatch(setIsLoading(true));
      },
      success: (filterOptions: FiltersMap) => {
        batch(() => {
          dispatch(setFilterOptions(filterOptions));
          dispatch(setIsLoading(false));
        });
      },
      failure: (error: Error) => {
        batch(() => {
          dispatch(setError(error));
          dispatch(setIsLoading(false));
        });
      },
    });
};
